import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { allApiAction } from "../../Redux/common/action";
import { URL } from "../../Redux/common/url";

const { Title, Text } = Typography;

const BOOKING_STATUSES = [
  "pending",
  "accepted",
  "confirmed",
  "cancelled",
];

const statusColors = {
  pending: "orange",
  accepted: "blue",
  confirmed: "green",
  cancelled: "red",
};

const Bookings = () => {
  const { orderId, functionId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [updatingId, setUpdatingId] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const bookingList = useSelector(
    (state) => state?.allApi?.booking_list || {}
  );

  const bookings = bookingList?.data || [];

  useEffect(() => {
    dispatch(
      allApiAction.getBookings({
        order_function_id: functionId,
        page,
        status: statusFilter,
      })
    );
  }, [dispatch, functionId, page, statusFilter]);

  const getImageUrl = (path) => {
    if (!path) return null;

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return `${URL.API_BASE_URL.replace(/\/$/, "")}/${path.replace(
      /^\//,
      ""
    )}`;
  };

  const refreshBookings = () => {
    return dispatch(
      allApiAction.getBookings({
        order_function_id: functionId,
        page,
        status: statusFilter,
      })
    );
  };

  const updateStatus = async (booking, status, reason = "") => {
    setUpdatingId(booking.id);

    try {
      const result = await dispatch(
        allApiAction.updateBookingStatus({
          booking_id: booking.id,
          status,
          ...(status === "cancelled" && reason
            ? { cancel_reason: reason }
            : {}),
        })
      );

      if (result?.status) {
        if (status === "cancelled") {
          setCancelModalOpen(false);
          setSelectedBooking(null);
          setCancelReason("");
        }

        await refreshBookings();
      }
    } catch (error) {
      // The Redux action displays the API error.
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusChange = (booking, status) => {
    if (booking.status === status) return;

    if (status === "cancelled") {
      setSelectedBooking(booking);
      setCancelReason("");
      setCancelModalOpen(true);
      return;
    }

    Modal.confirm({
      title: "Update booking status?",
      content: `Change booking #${booking.id} to ${status}?`,
      okText: "Update",
      cancelText: "Cancel",
      onOk: () => updateStatus(booking, status),
    });
  };

  const columns = [
    {
      title: "Booking ID",
      dataIndex: "id",
      key: "id",
      width: 110,
      render: (id) => `#${id}`,
    },
    {
      title: "Venue",
      key: "venue",
      render: (_, booking) => (
        <Space size={12}>
          {booking?.venue?.image && (
            <img
              src={getImageUrl(booking.venue.image)}
              alt={booking?.venue?.name || "Venue"}
              style={{
                width: 65,
                height: 50,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          )}
          <div>
            <Text strong>
              {booking?.venue?.name || "-"}
            </Text>
            <br />
            <Text type="secondary">
              Venue ID: {booking?.venue_id || "-"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, booking) =>
        booking?.venue?.amount != null
          ? `₹${Number(booking.venue.amount).toLocaleString("en-IN")}`
          : "-",
    },
    {
      title: "Current Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>
          {(status || "pending").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) =>
        date ? moment(date).format("DD-MM-YYYY hh:mm a") : "-",
    },
    {
      title: "Update Status",
      key: "update_status",
      width: 190,
      render: (_, booking) => (
        <Select
          value={booking.status}
          style={{ width: 160 }}
          loading={updatingId === booking.id}
          disabled={updatingId !== null}
          options={BOOKING_STATUSES.map((status) => ({
            value: status,
            label: status.charAt(0).toUpperCase() + status.slice(1),
          }))}
          onChange={(status) =>
            handleStatusChange(booking, status)
          }
        />
      ),
    },
  ];

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;

    await updateStatus(
      selectedBooking,
      "cancelled",
      cancelReason.trim()
    );
  };

  return (
    <div className="app-container">
      <Card>
        <Space
          align="center"
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
          wrap
        >
          <div>
            <Title level={3} style={{ marginBottom: 5 }}>
              Venue Bookings
            </Title>
            <Text type="secondary">
              Order #{orderId} · Function ID: {functionId}
            </Text>
          </div>

          <Button onClick={() => navigate(`/orders/${orderId}`)}>
            Back to Order
          </Button>
        </Space>

        <Space style={{ marginBottom: 16 }} wrap>
          <Text strong>Filter by status:</Text>

          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: 180 }}
            value={statusFilter}
            options={BOOKING_STATUSES.map((status) => ({
              value: status,
              label: status.charAt(0).toUpperCase() + status.slice(1),
            }))}
            onChange={(value) => {
              setPage(1);
              setStatusFilter(value);
            }}
          />
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={bookings}
          scroll={{ x: 1000 }}
          pagination={{
            current: bookingList?.current_page || page,
            pageSize: Number(bookingList?.per_page) || 10,
            total: Number(bookingList?.total) || 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (currentPage) => setPage(currentPage),
          }}
          locale={{
            emptyText: "No bookings found for this function.",
          }}
        />
      </Card>

      <Modal
        title="Cancel Venue Booking"
        open={cancelModalOpen}
        onCancel={() => {
          setCancelModalOpen(false);
          setSelectedBooking(null);
          setCancelReason("");
        }}
        onOk={handleCancelBooking}
        okText="Confirm Cancellation"
        okButtonProps={{
          danger: true,
          loading: updatingId === selectedBooking?.id,
        }}
        confirmLoading={updatingId === selectedBooking?.id}
      >
        <p>
          Cancel booking #{selectedBooking?.id} for{" "}
          <strong>
            {selectedBooking?.venue?.name || "this venue"}
          </strong>
          ?
        </p>

        <Input.TextArea
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Enter cancellation reason (optional)"
        />
      </Modal>
    </div>
  );
};

export default Bookings;